import {
  CheckCircleOutlined,
  FileExclamationOutlined,
} from '@ant-design/icons';
import { Form, Input, message, Modal, Space, Typography, Upload } from 'antd';
import {
  RcFile,
  UploadChangeParam,
  UploadFile,
} from 'antd/lib/upload/interface';
import axios, { CancelTokenSource } from 'axios';
import JSZip from 'jszip';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PlanogramMediaService from '../../../../api/planogram-media/service';
import PlanogramService from '../../../../api/planogram/services';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { getClient } from '../../../../store/features/clients/clientsSlice';
import { updatePlanogram } from '../../../../store/features/planogram/planogramSlice';
import { getCurrentUser } from '../../../../store/features/users/usersSlice';
import Button from '../../../elements/Button';
import FormWrapper from '../../../elements/FormWrapper';

const { Dragger } = Upload;
const { Title, Text } = Typography;

interface UploadedMediaData {
  objectKey: string;
  name: string;
  size: number;
  media_id: string;
  media_version_id: string;
  uuid: string;
}

enum FormStep {
  UPLOAD = 'upload',
  SUCCESS = 'success',
  CREATE_PROJECT = 'create_project',
  FINAL_SUCCESS = 'final_success',
}

const RSSegmentUploadForm = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();

  const [currentStep, setCurrentStep] = useState<FormStep>(FormStep.UPLOAD);

  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource | null>(null);

  const [uploadedMediaData, setUploadedMediaData] =
    useState<UploadedMediaData | null>(null);

  const [creatingProject, setCreatingProject] = useState(false);

  const { currentUser } = useAppSelector((state) => state.users);
  const { client } = useAppSelector((state) => state.clients);

  useEffect(() => {
    dispatch(getCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    if (currentUser?.client_id) {
      dispatch(getClient({ id: currentUser.client_id }));
    }
  }, [dispatch, currentUser?.client_id]);

  const validateUrlMatch = useCallback(
    (libraryPath: string, customEndpoint: string): boolean => {
      const extractDomainPart = (url: string): string => {
        try {
          const match = url.match(/[^.]+\.readysetvr\.com/);
          return match ? match[0] : '';
        } catch (error) {
          return '';
        }
      };

      const libraryDomain = extractDomainPart(libraryPath);
      const endpointDomain = extractDomainPart(customEndpoint);

      return libraryDomain !== '' && libraryDomain === endpointDomain;
    },
    []
  );

  const showAuthorizationError = () => {
    Modal.error({
      title: 'Authorization Required',
      content:
        'The planogram you are attempting to upload requires access to a product library that you are not authorized to use. Please reach out to your ReadySet representative for more information.',
      okText: 'Close',
      centered: true,
    });
  };

  const analyzeRSSegmentFile = useCallback(
    async (file: File): Promise<boolean> => {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const zip = new JSZip();
        const zipContents = await zip.loadAsync(arrayBuffer);

        for (const [fileName, zipEntry] of Object.entries(zipContents.files)) {
          if (!zipEntry.dir) {
            const extension = fileName.split('.').pop()?.toLowerCase();
            if (['json', 'txt', 'xml', 'csv'].includes(extension || '')) {
              const content = await zipEntry.async('string');
              try {
                const jsonContent = JSON.parse(content);

                // Validate customEndpoint exists
                if (
                  !jsonContent.customEndpoint ||
                  jsonContent.customEndpoint.trim() === ''
                ) {
                  showAuthorizationError();
                  return false;
                }

                // Validate client library path is configured
                if (!client?.library_path_2d) {
                  message.error('Client library path is not configured.');
                  return false;
                }

                // Validate domain matching
                const isValid = validateUrlMatch(
                  client.library_path_2d,
                  jsonContent.customEndpoint
                );
                if (isValid) {
                  return true;
                } else {
                  showAuthorizationError();
                  return false;
                }
              } catch (e) {
                console.error('Failed to parse JSON content:', e);
                message.error('Invalid file format');
                return false;
              }
            }
          }
        }

        message.error('No valid configuration found in the file');
        return false;
      } catch (error) {
        console.error('Error analyzing file:', error);
        message.error('Failed to analyze file');
        return false;
      }
    },
    [client, validateUrlMatch]
  );

  const uploadFileToS3 = useCallback(async (signedUrl: string, file: File) => {
    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    try {
      await axios.put(signedUrl, file, {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        cancelToken: source.token,
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Upload canceled by the user');
        throw new Error('Upload canceled by the user');
      } else {
        console.error('S3 upload error:', error);
        throw new Error(`S3 upload error for file: ${file?.name}`);
      }
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (fileList.length === 0) {
      message.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);
      const file = fileList[0].originFileObj;
      if (!file) {
        throw new Error('No file selected');
      }

      // Step 1: Validate .rssegment file
      const isValid = await analyzeRSSegmentFile(file);
      if (!isValid) {
        setUploading(false);
        return;
      }

      // Step 2: Create temporary planogram to get UUID for media upload
      const tempPlanogramResponse = await PlanogramService().createPlanogram({
        name: `Temp - ${file.name}`,
        description: 'Temporary planogram for file upload - will be updated',
      });

      // Step 3: Get signed URL for planogram media upload
      const { data } = await PlanogramMediaService().getSignedUploadUrl(
        file.name,
        tempPlanogramResponse.uuid
      );

      // Step 4: Upload file to S3
      await uploadFileToS3(data.signedUrl, file);

      // Step 5: Notify media service of completed upload
      await PlanogramMediaService().mediaUploaded({
        objectKey: data.objectKey,
        name: file.name,
        size: file.size,
        uuid: tempPlanogramResponse.uuid,
        media_id: data.media_id,
        media_version_id: data.media_version_id,
        obj: { status: 'active' }, // Set as active since planogram exists
      });

      // Store media data for later use
      setUploadedMediaData({
        objectKey: data.objectKey,
        name: file.name,
        size: file.size,
        media_id: data.media_id,
        media_version_id: data.media_version_id,
        uuid: tempPlanogramResponse.uuid,
      });

      // Move to success step
      setCurrentStep(FormStep.SUCCESS);
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload .rssegment file');
    } finally {
      setUploading(false);
    }
  }, [fileList, analyzeRSSegmentFile, uploadFileToS3]);

  const handleCreateProject = useCallback(
    async (values: { name: string; description?: string }) => {
      if (!uploadedMediaData) {
        message.error('No uploaded file data found');
        return;
      }

      try {
        setCreatingProject(true);

        // Update the existing planogram with user-provided details
        await dispatch(
          updatePlanogram({
            planogramId: uploadedMediaData.uuid,
            planogram: {
              name: values.name,
              description:
                values.description ||
                `Created from uploaded .rssegment file: ${uploadedMediaData.name}`,
            },
          })
        ).unwrap();

        // Move to final success step
        setCurrentStep(FormStep.FINAL_SUCCESS);
      } catch (error) {
        console.error('Project creation error:', error);
        message.error('Failed to update planogram project');
      } finally {
        setCreatingProject(false);
      }
    },
    [uploadedMediaData, dispatch]
  );

  const beforeUpload = (file: RcFile): boolean => {
    if (!file.name.endsWith('.rssegment')) {
      message.error('Please upload a .rssegment file');
      return false;
    }
    return false;
  };

  const onChange = (info: UploadChangeParam<UploadFile>) => {
    setFileList(info.fileList);
  };

  const onRemove = (file: UploadFile) => {
    setFileList((prevList) => prevList.filter((item) => item.uid !== file.uid));
  };

  const handleCancel = () => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel('Upload cancelled by user');
    }

    if (currentStep === FormStep.FINAL_SUCCESS) {
      history.goBack();
      return;
    }

    if (currentStep === FormStep.CREATE_PROJECT) {
      setCurrentStep(FormStep.SUCCESS);
      return;
    }

    if (currentStep === FormStep.SUCCESS) {
      setCurrentStep(FormStep.UPLOAD);
      setUploadedMediaData(null);
      return;
    }

    history.goBack();
  };

  const renderUploadForm = () => (
    <Form layout="vertical">
      <Form.Item
        name="file"
        rules={[{ required: true, message: 'Please select a file to upload' }]}
      >
        <Dragger
          accept=".rssegment"
          beforeUpload={beforeUpload}
          maxCount={1}
          multiple={false}
          onChange={onChange}
          onRemove={onRemove}
          fileList={fileList}
        >
          <Space size="middle" direction="vertical">
            <p>
              <FileExclamationOutlined style={{ fontSize: '52px' }} />
            </p>
            <p className="dragger-input-text">
              Click or drag .rssegment file to this area to upload
            </p>
            <p style={{ color: '#666', fontSize: '14px' }}>
              This will create a new planogram project with your .rssegment file
            </p>
          </Space>
        </Dragger>
      </Form.Item>
      <Form.Item>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Button
            type="primary"
            onClick={handleUpload}
            loading={uploading}
            disabled={fileList.length === 0 || uploading}
            style={{ width: '100%' }}
          >
            {uploading ? 'Uploading...' : 'Upload File'}
          </Button>
          <Button
            type="default"
            onClick={handleCancel}
            style={{ width: '100%' }}
          >
            Cancel
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );

  const renderSuccessMessage = () => (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%', textAlign: 'center' }}
    >
      <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a' }} />
      <Title level={3}>File Uploaded Successfully!</Title>
      <Text style={{ fontSize: '16px', color: '#666', textAlign: 'left' }}>
        Your <strong>{uploadedMediaData?.name}</strong> has been uploaded
        successfully.
        <br />
        It will be used when creating your planogram project.
        <br />
        <br />
      </Text>
      <Space
        direction="vertical"
        size="small"
        style={{ width: '100%', marginTop: '24px' }}
      >
        <Button
          type="primary"
          onClick={() => setCurrentStep(FormStep.CREATE_PROJECT)}
          style={{ width: '100%' }}
        >
          Create Planogram Project
        </Button>
      </Space>
    </Space>
  );

  const renderProjectForm = () => (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={handleCreateProject}
    >
      <Form.Item
        name="name"
        label="Project Name"
        rules={[{ required: true, message: 'Project name is required' }]}
      >
        <Input placeholder="Enter project name" />
      </Form.Item>
      <Form.Item name="description" label="Description">
        <Input.TextArea
          placeholder="Enter project description (optional)"
          rows={6}
        />
      </Form.Item>
      <Form.Item>
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Button
            loading={creatingProject}
            htmlType="submit"
            type="primary"
            style={{ width: '100%' }}
          >
            {creatingProject ? 'Creating Project...' : 'Create Project'}
          </Button>
          <Button onClick={handleCancel} style={{ width: '100%' }}>
            Back
          </Button>
        </Space>
      </Form.Item>
    </Form>
  );

  const renderFinalSuccess = () => (
    <Space
      direction="vertical"
      size="large"
      style={{ width: '100%', textAlign: 'center' }}
    >
      <CheckCircleOutlined style={{ fontSize: '64px', color: '#52c41a' }} />
      <Title level={3}>Planogram Project Created!</Title>
      <Text style={{ fontSize: '16px', color: '#666' }}>
        Your planogram project has been created successfully with the uploaded
        .rssegment file.
        <br />
        You can now access it from the planogram list.
      </Text>
      <Space
        direction="vertical"
        size="small"
        style={{ width: '100%', marginTop: '24px' }}
      >
        <Button
          type="primary"
          onClick={() => history.goBack()}
          style={{ width: '100%' }}
        >
          Close
        </Button>
      </Space>
    </Space>
  );

  const getTitle = () => {
    switch (currentStep) {
      case FormStep.UPLOAD:
        return 'Upload RS Segment';
      case FormStep.SUCCESS:
        return 'Upload Complete';
      case FormStep.CREATE_PROJECT:
        return 'Create Planogram Project';
      case FormStep.FINAL_SUCCESS:
        return 'Project Created';
      default:
        return 'Upload RS Segment';
    }
  };

  const getCurrentStepContent = () => {
    switch (currentStep) {
      case FormStep.UPLOAD:
        return renderUploadForm();
      case FormStep.SUCCESS:
        return renderSuccessMessage();
      case FormStep.CREATE_PROJECT:
        return renderProjectForm();
      case FormStep.FINAL_SUCCESS:
        return renderFinalSuccess();
      default:
        return renderUploadForm();
    }
  };

  return (
    <FormWrapper title={getTitle()} onClose={handleCancel}>
      {getCurrentStepContent()}
    </FormWrapper>
  );
};

export default RSSegmentUploadForm;
